import {
  CircleArrowDown,
  CircleArrowUp,
  CircleHelp,
  Minus,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
} from 'lucide-react';
import { Fragment } from 'react';

import { useIntl } from '../../hooks';
import { formatOptionalNumber } from '../../lib/number-format';
import { IdentityRule, MotionDirection, type SignalTrack } from '../../types';
import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui';

const getConfidenceLabel = (value: number | null | undefined) =>
  typeof value === 'number' ? `${Math.round(value * 100)}%` : '--';

const getIdentityVariant = (
  identityRule: IdentityRule | null | undefined,
): 'destructive' | 'outline' | 'success' | 'warning-light' => {
  switch (identityRule) {
    case IdentityRule.HOSTILE:
      return 'destructive';
    case IdentityRule.FRIENDLY:
      return 'success';
    case IdentityRule.NEUTRAL:
      return 'warning-light';
    default:
      return 'outline';
  }
};

const getMotionVariant = (
  motionDirection: MotionDirection | null | undefined,
): 'destructive' | 'outline' | 'secondary' | 'success' => {
  switch (motionDirection) {
    case MotionDirection.APPROACHING:
      return 'destructive';
    case MotionDirection.RECEDING:
      return 'success';
    case MotionDirection.STATIC:
      return 'secondary';
    default:
      return 'outline';
  }
};

function MotionIcon({
  motionDirection,
  className = 'h-3 w-3',
}: {
  motionDirection: MotionDirection | null | undefined;
  className?: string;
}) {
  switch (motionDirection) {
    case MotionDirection.APPROACHING:
      return <CircleArrowDown className={className} />;
    case MotionDirection.RECEDING:
      return <CircleArrowUp className={className} />;
    case MotionDirection.STATIC:
      return <Minus className={className} />;
    default:
      return <CircleHelp className={className} />;
  }
}

function IdentityIcon({
  identityRule,
  className = 'h-3 w-3',
}: {
  identityRule: IdentityRule | null | undefined;
  className?: string;
}) {
  switch (identityRule) {
    case IdentityRule.HOSTILE:
      return <ShieldAlert className={className} />;
    case IdentityRule.FRIENDLY:
      return <ShieldCheck className={className} />;
    case IdentityRule.NEUTRAL:
      return <Shield className={className} />;
    default:
      return <ShieldQuestion className={className} />;
  }
}

function SignalTrackMetrics({ signalTrack }: { signalTrack: SignalTrack }) {
  const intl = useIntl();
  const rows: { label: string; value: string }[] = [];

  if (signalTrack.avgLevelDbm != null) {
    rows.push({
      label: intl.formatMessage({ id: 'detections.table.track.avgLevel' }),
      value: `${formatOptionalNumber(signalTrack.avgLevelDbm)} dBm`,
    });
  }
  if (signalTrack.avgSnrDb != null) {
    rows.push({
      label: intl.formatMessage({ id: 'detections.table.track.avgSnr' }),
      value: `${formatOptionalNumber(signalTrack.avgSnrDb)} dB`,
    });
  }
  if (signalTrack.levelSlopeDbPerSec != null) {
    const sign = signalTrack.levelSlopeDbPerSec >= 0 ? '+' : '';
    rows.push({
      label: intl.formatMessage({ id: 'detections.table.track.levelSlope' }),
      value: `${sign}${formatOptionalNumber(signalTrack.levelSlopeDbPerSec)} dB/s`,
    });
  }
  if (signalTrack.snrSlopeDbPerSec != null) {
    const sign = signalTrack.snrSlopeDbPerSec >= 0 ? '+' : '';
    rows.push({
      label: intl.formatMessage({ id: 'detections.table.track.snrSlope' }),
      value: `${sign}${formatOptionalNumber(signalTrack.snrSlopeDbPerSec)} dB/s`,
    });
  }
  if (signalTrack.detectionRate != null) {
    rows.push({
      label: intl.formatMessage({ id: 'detections.table.track.detectionRate' }),
      value: `${formatOptionalNumber(signalTrack.detectionRate)} /s`,
    });
  }
  if (signalTrack.suppressionCount > 0) {
    rows.push({
      label: intl.formatMessage({ id: 'detections.table.track.suppressionCount' }),
      value: String(signalTrack.suppressionCount),
    });
  }

  if (rows.length === 0) return null;

  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
      {rows.map(({ label, value }) => (
        <Fragment key={label}>
          <span className="text-muted-foreground whitespace-nowrap">{label}</span>
          <span className="font-mono tabular-nums">{value}</span>
        </Fragment>
      ))}
    </div>
  );
}

export function renderMotionDirectionIcon(
  motionDirection: MotionDirection | null | undefined,
  confidence: number | null | undefined,
  label: string,
  signalTrack?: SignalTrack | null,
) {
  const iconColorClass =
    motionDirection === MotionDirection.APPROACHING
      ? 'text-destructive'
      : motionDirection === MotionDirection.RECEDING
        ? 'text-success'
        : motionDirection === MotionDirection.STATIC
          ? 'text-foreground/60'
          : 'text-foreground/40';

  const confidencePct = typeof confidence === 'number' ? Math.round(confidence * 100) : null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex flex-col items-center justify-center gap-0.5 cursor-default"
            data-testid="motion-direction-icon"
          >
            <MotionIcon motionDirection={motionDirection} className={`h-4 w-4 ${iconColorClass}`} />
            {confidencePct !== null && (
              <span className="text-[9px] leading-none tabular-nums text-muted-foreground">
                {confidencePct}%
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{label}</p>
          {confidencePct !== null && (
            <p className="text-muted-foreground text-xs">{confidencePct}%</p>
          )}
          {signalTrack && <SignalTrackMetrics signalTrack={signalTrack} />}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function SignalTrackSummary({ signalTrack }: { signalTrack?: SignalTrack | null }) {
  const intl = useIntl();

  if (!signalTrack) {
    return (
      <div className="text-xs text-muted-foreground">
        {intl.formatMessage({ id: 'detections.table.trackPending' })}
      </div>
    );
  }

  const motionDirection = signalTrack.motionDirection ?? MotionDirection.UNKNOWN;
  const identityRule = signalTrack.identityRule ?? IdentityRule.UNKNOWN;
  const motionLabel = intl.formatMessage({
    id: `detections.table.motionDirections.${motionDirection}`,
  });
  const identityLabel = intl.formatMessage({
    id: `detections.table.identityRules.${identityRule}`,
  });

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="signal-track-summary">
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant={getMotionVariant(motionDirection)} className="gap-1 cursor-default">
              <MotionIcon motionDirection={motionDirection} />
              {intl.formatMessage({ id: 'detections.table.motion' })}: {motionLabel}
              <span className="opacity-70">
                ({getConfidenceLabel(signalTrack.motionDirectionConfidence)})
              </span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="font-medium mb-1">
              {intl.formatMessage({ id: 'detections.table.track.signalMetrics' })}
            </p>
            <SignalTrackMetrics signalTrack={signalTrack} />
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant={getIdentityVariant(identityRule)} className="gap-1 cursor-default">
              <IdentityIcon identityRule={identityRule} />
              {intl.formatMessage({ id: 'detections.table.identity' })}: {identityLabel}
              <span className="opacity-70">
                ({getConfidenceLabel(signalTrack.identityRuleConfidence)})
              </span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="text-xs">
              {intl.formatMessage({ id: 'detections.table.track.identityNote' })}
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
